<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="open"
        class="modal-backdrop"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        @keydown.escape="handleClose"
        @click.self="handleOverlayClick"
      >
        <div
          ref="modalRef"
          :class="['modal', `modal--${size}`]"
          @keydown.tab="handleTab"
        >
          <div v-if="title || closeButton" class="modal-header">
            <h2 :id="titleId" class="modal-title">{{ title }}</h2>
            <button
              v-if="closeButton"
              class="modal-close"
              :aria-label="t('common.close_dialog')"
              @click="handleClose"
            >
              <X :size="18" />
            </button>
          </div>

          <div class="modal-body">
            <slot />
          </div>

          <div v-if="$slots.footer" class="modal-footer">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, useId } from 'vue'
import { useI18n } from 'vue-i18n'
import { X } from 'lucide-vue-next'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    open: boolean
    title?: string
    closeButton?: boolean
    size?: 'sm' | 'md' | 'lg'
    closeable?: boolean
  }>(),
  {
    title: '',
    closeButton: true,
    size: 'md',
    closeable: true,
  }
)

const emit = defineEmits<{
  close: []
  confirm: []
}>()

const modalRef = ref<HTMLElement | null>(null)
const titleId = `modal-title-${useId()}`
let previouslyFocused: HTMLElement | null = null

function handleClose() {
  emit('close')
}

function handleOverlayClick() {
  if (props.closeable) {
    handleClose()
  }
}

function getFocusableElements(): HTMLElement[] {
  if (!modalRef.value) return []
  return Array.from(
    modalRef.value.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  )
}

function handleTab(e: KeyboardEvent) {
  const focusable = getFocusableElements()
  if (focusable.length === 0) return

  const first = focusable[0]
  const last = focusable[focusable.length - 1]

  if (e.shiftKey) {
    if (document.activeElement === first) {
      e.preventDefault()
      last.focus()
    }
  } else {
    if (document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }
}

watch(
  () => props.open,
  async (isOpen) => {
    if (isOpen) {
      previouslyFocused = document.activeElement as HTMLElement
      document.body.style.overflow = 'hidden'
      await nextTick()
      const focusable = getFocusableElements()
      if (focusable.length > 0) {
        focusable[0].focus()
      } else {
        modalRef.value?.focus()
      }
    } else {
      document.body.style.overflow = ''
      previouslyFocused?.focus()
      previouslyFocused = null
    }
  }
)
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background-color: var(--bg-modal, rgba(15, 25, 35, 0.9));
  z-index: var(--z-modal, 400);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-4, 16px);
}

.modal {
  background-color: var(--bg-secondary, #1a2c38);
  border-radius: var(--radius-lg, 12px);
  box-shadow: var(--shadow-lg, 0 8px 24px rgba(0, 0, 0, 0.5));
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.modal--sm {
  max-width: 480px;
}

.modal--md {
  max-width: 640px;
}

.modal--lg {
  max-width: 800px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4, 16px) var(--space-6, 24px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  flex-shrink: 0;
}

.modal-title {
  font-size: var(--font-size-h2, 1.125rem);
  font-weight: var(--font-weight-semibold, 600);
  color: var(--text-primary, #fff);
  margin: 0;
  line-height: var(--line-height-heading, 1.2);
}

.modal-close {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-tertiary, #7f8fa4);
  cursor: pointer;
  border-radius: var(--radius-md, 8px);
  transition: all var(--transition-fast, 150ms ease);
  background: none;
  border: none;
  font-size: 14px;
  flex-shrink: 0;
}

.modal-close:hover {
  color: var(--text-primary, #fff);
  background-color: var(--bg-tertiary, #213743);
}

.modal-body {
  padding: var(--space-6, 24px);
  overflow-y: auto;
  flex: 1;
  color: var(--text-secondary, #b1bad3);
  font-size: var(--font-size-body, 0.875rem);
  line-height: var(--line-height-body, 1.5);
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-3, 12px);
  padding: var(--space-4, 16px) var(--space-6, 24px);
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  flex-shrink: 0;
}

/* Transition: backdrop fade + modal scale */
.modal-enter-active .modal-backdrop,
.modal-enter-active {
  animation: modalBackdropIn var(--transition-normal, 250ms ease) forwards;
}

.modal-leave-active {
  animation: modalBackdropOut 200ms ease forwards;
}

.modal-enter-active .modal {
  animation: modalScaleIn var(--transition-normal, 250ms ease) forwards;
}

.modal-leave-active .modal {
  animation: modalScaleOut 200ms ease forwards;
}

@keyframes modalBackdropIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes modalBackdropOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@keyframes modalScaleIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes modalScaleOut {
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(0.9);
  }
}

/* Mobile: modal slides up from bottom */
@media (max-width: 767px) {
  .modal-backdrop {
    align-items: flex-end;
    padding: 0;
  }

  .modal {
    border-radius: var(--radius-lg, 12px) var(--radius-lg, 12px) 0 0;
    max-height: 85vh;
  }
}
</style>
